import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SUPPORTED_LOCALES = ['en'];
const DEFAULT_LOCALE = 'en';
const THAI_LOCALE = 'th';

const LocaleContext = createContext();

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => {
    const saved = localStorage.getItem('preferred-locale');
    const browserLocale = navigator.language.split('-')[0];
    
    return saved || 
           (SUPPORTED_LOCALES.includes(browserLocale) ? browserLocale : DEFAULT_LOCALE);
  });
  
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMessages = useCallback(async (targetLocale) => {
    setLoading(true);
    setError(null);

    try {
      const messageModule = await import(`../locales/${targetLocale}.json`);
      setMessages(messageModule.default || messageModule);
    } catch (err) {
      setError(`Failed to load language: ${targetLocale}`);
      
      if (targetLocale !== DEFAULT_LOCALE) {
        try {
          const fallbackModule = await import(`../locales/${DEFAULT_LOCALE}.json`);
          setMessages(fallbackModule.default || fallbackModule);
          setLocaleState(DEFAULT_LOCALE);
        } catch (fallbackErr) {
          setError('Failed to load language files');
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages(locale);
  }, [locale, loadMessages]);

  const setLocale = useCallback((newLocale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setLocaleState(newLocale);
      localStorage.setItem('preferred-locale', newLocale);
      
      document.documentElement.lang = newLocale;
    }
  }, []);

  const t = useCallback((key, params = {}) => {
    if (!key) return '';

    const keys = key.split('.');
    let message = messages;
    
    for (const k of keys) {
      if (message && typeof message === 'object' && k in message) {
        message = message[k];
      } else {
        return key;
      }
    }

    if (typeof message !== 'string') {
      return key;
    }

    let result = message;
    Object.entries(params).forEach(([param, value]) => {
      const placeholder = new RegExp(`\\{${param}\\}`, 'g');
      result = result.replace(placeholder, String(value));
    });

    return result;
  }, [messages, locale]);

  const formatDate = useCallback((date, options = {}) => {
    try {
      return new Intl.DateTimeFormat(THAI_LOCALE, options).format(new Date(date));
    } catch (err) {
      return date.toString();
    }
  }, []);

  const formatNumber = useCallback((number, options = {}) => {
    try {
      return new Intl.NumberFormat(THAI_LOCALE, options).format(number);
    } catch (err) {
      return number.toString();
    }
  }, []);

  const formatCurrency = useCallback((amount, currency = 'THB', options = {}) => {
    try {
      return new Intl.NumberFormat(THAI_LOCALE, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options
      }).format(amount);
    } catch (err) {
      return `${amount} ${currency}`;
    }
  }, []);

  const getTextDirection = useCallback(() => {
    return 'ltr';
  }, []);

  const contextValue = {
    locale,
    messages,
    loading,
    error,
    supportedLocales: SUPPORTED_LOCALES,
    
    setLocale,
    t,
    formatDate,
    formatNumber,
    formatCurrency,
    getTextDirection,
    
    isRTL: getTextDirection() === 'rtl'
  };

  return (
    <LocaleContext.Provider value={contextValue}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocaleContext = () => {
  const context = useContext(LocaleContext);
  
  if (!context) {
    throw new Error('useLocaleContext must be used within a LocaleProvider');
  }
  
  return context;
};

export default LocaleContext;